/**
 * The route planner's rules over a list of stops, as pure functions. Generalised from the Mad Monkey planner
 * (partners/madmonkey/plannerLogic.ts), which is welded to its hostel list: here a stop carries its own name and
 * coordinates, so the same rules serve a ready-made route's stops and a route built from a country's destinations.
 */

export interface LatLng {
	lat: number;
	lng: number;
}

export interface Stop extends LatLng {
	/** unique within a route: the destination's slug where there is one, else the name; a repeat of a place gets a suffix */
	key: string;
	name: string;
	/** full English country name, as createTripFromRoute takes it */
	country: string;
	/** MIN_NIGHTS to MAX_NIGHTS */
	nights: number;
}

export const MIN_NIGHTS = 1;
/** createTripFromRoute takes 1 to 60 nights per stop and 1 to 30 stops (graphql/schemas/routeTrip.ts) */
export const MAX_NIGHTS = 60;
export const MAX_STOPS = 30;
/** How far the planner's "+" goes. A ready-made route can bring a longer stop, which the buttons then only shorten. */
export const STEPPER_MAX_NIGHTS = 14;

/** A stop with no nights (the payload can say 0) still takes one: the trip needs a night to put it on. */
export const clampNights = (n: unknown): number => {
	const nights = Math.round(Number(n));
	return Number.isFinite(nights) ? Math.max(MIN_NIGHTS, Math.min(MAX_NIGHTS, nights)) : MIN_NIGHTS;
};

/** "Ko Phi Phi Don" and "ko-phi-phi-don" and "Ko Phí Phi Don" are one place. */
export const normalizeName = (name: string): string =>
	name
		.normalize("NFD")
		.replace(/[̀-ͯ]/g, "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, " ")
		.trim();

export const keyOfName = (name: string): string => normalizeName(name).replace(/ /g, "-");

export interface StopSource extends LatLng {
	name: string;
	country: string;
	nights: number;
}

/**
 * A ready-made route's stops as planner stops. `keyFor` lets the caller key a stop by the destination it is (its slug);
 * a place the route visits twice (Bangkok first and last) keeps its key and gets "-2" on the second visit.
 */
export function routeToStops(stops: StopSource[] = [], keyFor: (stop: StopSource) => string = (s) => keyOfName(s.name)): Stop[] {
	const seen = new Map<string, number>();
	return stops.map((s) => {
		const base = keyFor(s) || "stop";
		const visit = (seen.get(base) ?? 0) + 1;
		seen.set(base, visit);
		return { key: visit > 1 ? `${base}-${visit}` : base, name: s.name, country: s.country, lat: s.lat, lng: s.lng, nights: clampNights(s.nights) };
	});
}

export const totalNights = (stops: { nights: number }[]): number => stops.reduce((total, s) => total + s.nights, 0);

const distance = (a: LatLng, b: LatLng): number => {
	const rad = Math.PI / 180;
	const h = Math.sin(((b.lat - a.lat) * rad) / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(((b.lng - a.lng) * rad) / 2) ** 2;
	return 12742 * Math.asin(Math.sqrt(h));
};

/** straight-line km between two places, rounded: the planner's distance chips */
export const km = (a: LatLng, b: LatLng): number => Math.round(distance(a, b));

/** First and last day of each stop: [[1, 2], [3, 5], ...] */
export function dayRanges(stops: { nights: number }[]): [number, number][] {
	let day = 1;
	return stops.map(({ nights }) => {
		const range: [number, number] = [day, day + nights - 1];
		day += nights;
		return range;
	});
}

/**
 * Put `stop` where it adds the least distance to the route: between the two stops it is cheapest to detour through, or at an
 * end. A tie goes to the later spot, so a first stop on an empty route and a stop that is equally far either way go last.
 * A stop that is already in the route stays where it is.
 */
export function insertAtLeastDistance(stops: Stop[], stop: Stop): Stop[] {
	if (stops.some((s) => s.key === stop.key)) return stops;
	let at = stops.length;
	let least = Infinity;
	for (let i = 0; i <= stops.length; i++) {
		const prev = stops[i - 1];
		const next = stops[i];
		const extra =
			prev && next ? distance(prev, stop) + distance(stop, next) - distance(prev, next) : prev ? distance(prev, stop) : next ? distance(stop, next) : 0;
		if (extra <= least) {
			least = extra;
			at = i;
		}
	}
	return [...stops.slice(0, at), stop, ...stops.slice(at)];
}

export const removeStop = (stops: Stop[], index: number): Stop[] => stops.filter((_, i) => i !== index);

export function changeNights(stops: Stop[], index: number, delta: number): Stop[] {
	return stops.map((s, i) => {
		if (i !== index) return s;
		const nights = clampNights(s.nights + delta);
		return { ...s, nights: delta > 0 ? Math.min(nights, Math.max(s.nights, STEPPER_MAX_NIGHTS)) : nights };
	});
}

/** "Near {last stop}": the closest of `candidates` (anything with a slug and a place) that are not in the route yet. */
export const nearest = <T extends LatLng & { slug: string }>(stops: Stop[], from: LatLng, candidates: T[], count = 3): T[] =>
	candidates
		.filter((c) => !stops.some((s) => s.key === c.slug))
		.sort((a, b) => distance(from, a) - distance(from, b))
		.slice(0, count);

/**
 * Whether a place's name is what the search box is after: every word typed starts a word of the name, whatever the case and
 * diacritics. "ko" finds "Koh Tao" and "Ko Lanta" alike, and "lan" finds "Ko Lanta".
 */
export function matchesQuery(name: string, query: string): boolean {
	const words = normalizeName(name).split(" ");
	return normalizeName(query)
		.split(" ")
		.filter(Boolean)
		.every((q) => words.some((w) => w.startsWith(q)));
}

/** Take the stop at `from` and put it where `to` is: what dragging a row onto another does. */
export function moveStop(stops: Stop[], from: number, to: number): Stop[] {
	if (from === to || !stops[from] || !stops[to]) return stops;
	const next = [...stops];
	const [moved] = next.splice(from, 1);
	next.splice(to, 0, moved);
	return next;
}

// ---- days -------------------------------------------------------------------------------------------------------
// A start date is a local `YYYY-MM-DD` day, never an instant: no timezone moves it to the day before.

const pad = (n: number): string => String(n).padStart(2, "0");

/** The local day of a date, as `YYYY-MM-DD` */
export const toDay = (date: Date): string => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** The local midnight a `YYYY-MM-DD` day begins at */
export function parseDay(day: string): Date {
	const [y, m, d] = day.split("-").map(Number);
	return new Date(y, m - 1, d);
}

export function addDays(day: string, days: number): string {
	const date = parseDay(day);
	date.setDate(date.getDate() + days);
	return toDay(date);
}

/** `month` is `YYYY-MM` */
export function shiftMonth(month: string, by: number): string {
	const [y, m] = month.split("-").map(Number);
	return toDay(new Date(y, m - 1 + by, 1)).slice(0, 7);
}

/** The cells of a month grid: `null` pads the first week, then one `YYYY-MM-DD` per day. */
export function monthCells(month: string, mondayFirst: boolean): (string | null)[] {
	const [y, m] = month.split("-").map(Number);
	const lead = (new Date(y, m - 1, 1).getDay() + (mondayFirst ? 6 : 0)) % 7;
	const days = new Date(y, m, 0).getDate();
	return [...Array<null>(lead).fill(null), ...Array.from({ length: days }, (_, i) => `${month}-${pad(i + 1)}`)];
}
